import { CodedProperty, NamedProperty, getFileNameWithExtension } from "sprint-common-web";

import {
    AudioFileRecordMetadataResponse,
    CleanVideo,
    EmbeddedSubtitle,
    EmbeddedSubtitleType,
    FileRecordStatusDetailsResponse,
    GetFileRecordDetailsResponse,
    isFileRecordRejectionDetailsResponse,
    OriginatorReference,
    VideoFileRecordMetadataResponse,
    VideoWithSubtitle
} from "../../../common/api/master-api/file-records/details/GetFileRecordDetailsResponse";
import { AspectRatio } from "../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { AspectType } from "../../../common/data/lookup-data-providers/AspectTypesDataProvider";
import { AncillaryContentSubtype } from "../../../common/types/AncillaryContentType";
import {
    ensureAncillaryContentDetails,
    ensureFullTvSeriesContentDetails,
    ensureMovieContentDetails,
    ContentDetails
} from "../../../common/types/ContentDetails";
import { ContentEpisodeVersion, ContentMovieVersion } from "../../../common/types/ContentReference";
import { ContentSubtype } from "../../../common/types/ContentSubtype";
import { FileRecordStatus, mapToFileRecordStatus } from "../../../common/types/FileRecordStatus";
import { MovieContentSubtype } from "../../../common/types/MovieContentSubtype";
import { RecordType } from "../../../common/types/RecordType";
import { Standard } from "../../../common/types/Standard";
import { StorageType } from "../../../common/types/StorageType";
import { TvSeriesContentSubtype } from "../../../common/types/TvSeriesContentSubtype";
import { VersionSummary } from "../../../common/types/VersionSummary";
import { FileRecordStatusDetails } from "../../file-records/common/types/FileRecordStatusDetails";

export type FileRecordDetails = VideoFileRecordDetails | AudioFileRecordDetails | SubtitleFileRecordDetails;

interface BaseFileRecordDetails {
    id: string;
    deliveryPath?: string;
    arrivalDate?: Date;
    originator?: OriginatorReference;
    branding?: string;
    deliveryId: string;
    deliveryFileName: string;
    status: FileRecordStatus;
    statusDetails?: FileRecordStatusDetails;
    content?: FileRecordContentDetails;
    isBeingProcessed?: boolean;
    storageType: StorageType;
    reference?: string;
    isNotificationApproved: boolean;
    isTrailer: boolean;
}

export interface VideoFileRecordDetails extends BaseFileRecordDetails {
    type: RecordType.Video;
    technical?: VideoFileRecordTechnical;
    soundtracks?: CodedProperty[];
    subtitle?: EmbeddedSubtitle;
}

export interface AudioFileRecordDetails extends BaseFileRecordDetails {
    type: RecordType.Audio;
    technical?: AudioFileRecordTechnical;
    soundtrack?: CodedProperty;
}

export interface SubtitleFileRecordDetails extends BaseFileRecordDetails {
    type: RecordType.Subtitle;
    subtitle?: CodedProperty;
}

export interface VideoFileRecordTechnical {
    aspectRatio?: AspectRatio;
    aspectType?: AspectType;
    encoding?: boolean;
    videoBitRate?: number;
    duration?: string;
    resolution?: string;
    standard?: Standard;
    framerate?: string;
}

export interface AudioFileRecordTechnical {
    resolution?: string;
    audioFormat?: string;
    duration?: string;
    sampleRate?: number;
    channelsCount?: number;
    audioBitRate?: number;
}

export interface VideoFileRecordLanguages {
    soundtracks?: CodedProperty[];
    subtitle?: CodedProperty;
}

export type FileRecordContentDetails = FileRecordMovieDetails | FileRecordTvSeriesDetails | FileRecordAncillaryDetails;

export interface FileRecordMovieDetails {
    contentSubtype: MovieContentSubtype;
    movie: NamedProperty;
    versions: ContentMovieVersion[];
}

export interface FileRecordTvSeriesDetails {
    contentSubtype: TvSeriesContentSubtype;
    tvSeries: NamedProperty;
    season: NamedProperty;
    episode: NamedProperty;
    versions: ContentEpisodeVersion[];
}

export interface FileRecordAncillaryDetails {
    contentSubtype: AncillaryContentSubtype;
    ancillary: NamedProperty;
    airline: CodedProperty;
    versionSummary: VersionSummary;
}

export const getFileRecordContentVersionIds = (contentReference: FileRecordContentDetails): string[] => {
    if (isMovieContent(contentReference)) {
        return contentReference.versions.map((version) => version.id);
    } else if (isTvSeriesContent(contentReference)) {
        return contentReference.versions.map((version) => version.id);
    } else if (isAncillaryContent(contentReference)) {
        return contentReference.versionSummary.ids;
    }

    throw new Error("Unsupported content reference type");
};

export const mapToFileRecordDetails = (response: GetFileRecordDetailsResponse): FileRecordDetails => {
    const baseDetails = getBaseFileRecordDetails(response);

    switch (response.recordType) {
        case RecordType.Video:
            return {
                ...baseDetails,
                type: RecordType.Video,
                subtitle: response.subtitle,
                soundtracks: response.soundtracks,
                technical: mapToVideoTechnical(response.metadata)
            };
        case RecordType.Audio:
            return {
                ...baseDetails,
                type: RecordType.Audio,
                soundtrack: response.soundtrack,
                technical: mapToAudioTechnical(response.metadata)
            };
        case RecordType.Subtitle:
            return {
                ...baseDetails,
                type: RecordType.Subtitle,
                subtitle: response.subtitle
            };
    }
};

const getBaseFileRecordDetails = (response: GetFileRecordDetailsResponse): BaseFileRecordDetails => {
    return {
        id: response.id,
        storageType: response.storageType,
        originator: response.delivery.originator,
        content: mapToContentDetails(response.content),
        deliveryFileName: getFileNameWithExtension(response.delivery.file.path),
        deliveryId: response.delivery.id,
        deliveryPath: response.delivery.path,
        arrivalDate: new Date(response.delivery.arrivalDate),
        status: mapToFileRecordStatus(response.status),
        reference: response.delivery.reference,
        isNotificationApproved: response.isNotificationApproved,
        statusDetails: mapToStatusDetails(response.statusDetails),
        isTrailer: response.isTrailer
    };
};

const mapToStatusDetails = (response?: FileRecordStatusDetailsResponse): FileRecordStatusDetails | undefined => {
    if (!response) {
        return;
    }

    return {
        reason: isFileRecordRejectionDetailsResponse(response) ? response.reason.name : undefined,
        details: response.details,
        courseOfAction: response.courseOfAction?.name,
        fixedBilling: response.fixedBilling?.name
    };
};

const mapToVideoTechnical = (response: VideoFileRecordMetadataResponse): VideoFileRecordTechnical => {
    return {
        duration: response.duration,
        framerate: response.framerate,
        aspectRatio: response.aspectRatio,
        aspectType: response.aspectType,
        encoding: response.encoding,
        videoBitRate: response.videoBitRate,
        resolution: response.resolution,
        standard: response.standard
    };
};

const mapToAudioTechnical = (response: AudioFileRecordMetadataResponse): AudioFileRecordTechnical => {
    return {
        audioFormat: response.audioFormat,
        resolution: response.resolution,
        duration: response.duration,
        sampleRate: response.sampleRate,
        channelsCount: response.channelsCount,
        audioBitRate: response.audioBitRate
    };
};

export const mapToContentDetails = (content?: ContentDetails): FileRecordContentDetails | undefined => {
    if (!content) {
        return undefined;
    }

    switch (content.subtype) {
        case ContentSubtype.Movie:
        case ContentSubtype.TvSingle:
        case ContentSubtype.FeatureDocumentary:
        case ContentSubtype.ShortFilm: {
            ensureMovieContentDetails(content);

            return {
                contentSubtype: content.subtype,
                movie: content.movie,
                versions: content.versions
            };
        }

        case ContentSubtype.TV: {
            ensureFullTvSeriesContentDetails(content);

            return {
                contentSubtype: content.subtype,
                tvSeries: content.tvSeries,
                episode: content.episode,
                season: content.season,
                versions: content.versions
            };
        }

        case ContentSubtype.Advert:
        case ContentSubtype.Promo:
        case ContentSubtype.Safety:
        case ContentSubtype.ClientSpecific:
        case ContentSubtype.IndentWarning: {
            ensureAncillaryContentDetails(content);

            return {
                contentSubtype: content.subtype,
                airline: content.airline,
                ancillary: content.ancillary,
                versionSummary: content.versionSummary
            };
        }

        default:
            return undefined;
    }
};

export const ensureAudioFileRecordDetails: (details: FileRecordDetails) => asserts details is AudioFileRecordDetails = (
    details: FileRecordDetails
) => {
    if (details.type !== RecordType.Audio) {
        throw Error(`File record type should be audio`);
    }
};

export const ensureVideoOrAudioFileRecordDetails: (
    details: FileRecordDetails
) => asserts details is VideoFileRecordDetails | AudioFileRecordDetails = (details: FileRecordDetails) => {
    if (![RecordType.Audio, RecordType.Video].includes(details.type)) {
        throw Error(`File record type should be video or audio`);
    }
};

export const ensureVideoOrSubtitleFileRecordDetails: (
    details: FileRecordDetails
) => asserts details is VideoFileRecordDetails | SubtitleFileRecordDetails = (details: FileRecordDetails) => {
    if (![RecordType.Subtitle, RecordType.Video].includes(details.type)) {
        throw Error(`File record type should be video or subtitle`);
    }
};

export const ensureVideoFileRecordDetails: (details: FileRecordDetails) => asserts details is VideoFileRecordDetails = (
    details: FileRecordDetails
) => {
    if (details.type !== RecordType.Video) {
        throw Error(`File record type should be video'`);
    }
};

export const ensureOnPremAudioDetails: (details: FileRecordDetails) => asserts details is AudioFileRecordDetails = (
    details: FileRecordDetails
) => {
    if (details.type !== RecordType.Audio) {
        throw Error(`File record type should be audio'`);
    }
};

export const ensureSubtitleFileRecordDetails: (
    details: FileRecordDetails
) => asserts details is SubtitleFileRecordDetails = (details: FileRecordDetails) => {
    if (details.type !== RecordType.Subtitle) {
        throw Error(`File record type should be subtitle'`);
    }
};

export const ensureFileRecordContent: (
    content?: FileRecordContentDetails
) => asserts content is FileRecordContentDetails = (content?: FileRecordContentDetails) => {
    if (!content) {
        throw new Error("File record content has to be specified");
    }
};

export const isSubtitleFileRecordDetails = (file: FileRecordDetails): file is SubtitleFileRecordDetails => {
    return file.type === RecordType.Subtitle;
};

export const isAncillaryContent = (content: FileRecordContentDetails): content is FileRecordAncillaryDetails => {
    return [
        ContentSubtype.Advert,
        ContentSubtype.Promo,
        ContentSubtype.Safety,
        ContentSubtype.IndentWarning,
        ContentSubtype.ClientSpecific
    ].includes(content.contentSubtype);
};

export const isMovieContent = (content: FileRecordContentDetails): content is FileRecordMovieDetails => {
    return [
        ContentSubtype.Movie,
        ContentSubtype.TvSingle,
        ContentSubtype.FeatureDocumentary,
        ContentSubtype.ShortFilm
    ].includes(content.contentSubtype);
};

export const isTvSeriesContent = (content: FileRecordContentDetails): content is FileRecordTvSeriesDetails => {
    return content.contentSubtype === ContentSubtype.TV;
};

export const isCleanVideo = (subtitle?: EmbeddedSubtitle): subtitle is CleanVideo => {
    return subtitle?.type === EmbeddedSubtitleType.CleanVideo;
};

export const isVideoWithSubtitle = (subtitle?: EmbeddedSubtitle): subtitle is VideoWithSubtitle => {
    return subtitle?.type === EmbeddedSubtitleType.Subtitle;
};

export const isFileRecordDetailsEditable = (fileRecordDetails: FileRecordDetails): boolean => {
    return ![FileRecordStatus.Rejected, FileRecordStatus.Destroyed, FileRecordStatus.QcDone].includes(
        fileRecordDetails.status
    );
};
